import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, ScrollText, Lock } from 'lucide-react';
import { Button } from '../../ui/button';
import { API_URL, auth, errMsg, rand } from '../financeApi';
import { PAY_STATUS, shiftMonth } from './payShared';
import { KidPaymentRow } from './KidPaymentRow';
import { RecordPaymentDialog } from './RecordPaymentDialog';
import { FeeDialog } from './FeeDialog';
import { KidHistoryDialog } from './KidHistoryDialog';
import { PaymentLogDialog } from './PaymentLogDialog';

const FILTERS = ['all', 'paid', 'part_paid', 'not_paid', 'no_fee'];

const Summary = ({ data, filter, setFilter }) => (
  <div className="space-y-3">
    <div className="grid grid-cols-3 gap-2 sm:gap-3">
      {[['Expected', data.totals.expected, 'text-slate-900'], ['Received', data.totals.received, 'text-emerald-700'], ['Still owed', data.totals.owed, 'text-red-700']].map(([l, v, c]) => (
        <div key={l} className="rounded-2xl border bg-white p-3 sm:p-4" data-testid={`pp-total-${l.toLowerCase().replace(' ', '-')}`}>
          <p className="text-xs text-muted-foreground">{l}</p>
          <p className={`text-base sm:text-2xl font-heading font-bold whitespace-nowrap ${c}`}>{rand(v)}</p>
        </div>
      ))}
    </div>
    <div className="flex flex-wrap gap-2" data-testid="pp-filters">
      {FILTERS.map((f) => (
        <button key={f} type="button" onClick={() => setFilter(f)} data-testid={`pp-filter-${f}`}
          className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${filter === f ? 'bg-emerald-900 text-white border-emerald-900' : 'bg-white hover:bg-muted/40'}`}>
          {f === 'all' ? `All (${data.rows.length})` : `${PAY_STATUS[f].label} (${data.counts[f]})`}
        </button>
      ))}
    </div>
  </div>
);

export const ParentPaymentsTab = ({ role, token, onChanged }) => {
  const [month, setMonth] = useState(null);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all');
  const [recording, setRecording] = useState(null);
  const [feeFor, setFeeFor] = useState(null);
  const [historyKid, setHistoryKid] = useState(null);
  const [showLog, setShowLog] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/finance/parent-payments`, { ...auth(token), params: month ? { month } : {} });
      setData(res.data);
    } catch (e) {
      toast.error(errMsg(e, 'Could not load parent payments'));
    }
  }, [token, month]);

  useEffect(() => { load(); }, [load]);

  const done = () => { load(); setRefreshKey((k) => k + 1); onChanged?.(); };

  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const rows = data.rows.filter((r) => filter === 'all' || r.month.status === filter);
  const atCurrent = data.month >= data.current_month;
  const locked = data.rows.length > 0 && data.rows.every((r) => !r.can_record);

  return (
    <section className="space-y-4" data-testid="parent-payments-tab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1 bg-white border rounded-full p-1">
          <Button size="icon" variant="ghost" className="rounded-full h-8 w-8" onClick={() => setMonth(shiftMonth(data.month, -1))} data-testid="pp-prev-month"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="px-2 font-semibold text-sm min-w-[8rem] text-center" data-testid="pp-month-label">{data.label}</span>
          <Button size="icon" variant="ghost" className="rounded-full h-8 w-8" disabled={atCurrent} onClick={() => setMonth(shiftMonth(data.month, 1))} data-testid="pp-next-month"><ChevronRight className="w-4 h-4" /></Button>
        </div>
        {role === 'transport' && <Button variant="outline" className="rounded-full" onClick={() => setShowLog(true)} data-testid="pp-open-log"><ScrollText className="w-4 h-4 mr-1" /> Change log</Button>}
      </div>
      {locked && <p className="flex items-center gap-2 text-sm rounded-xl bg-slate-100 p-3" data-testid="pp-locked-note"><Lock className="w-4 h-4" /> Parent Payments is part of the Finance Tracker. Start the free trial or the yearly plan to record payments. Saved records stay visible.</p>}
      <Summary data={data} filter={filter} setFilter={setFilter} />
      {data.rows.length === 0 && <p className="text-sm text-muted-foreground bg-white border rounded-xl p-4" data-testid="pp-empty">No children on your list yet.</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {rows.map((r) => (
          <KidPaymentRow key={r.kid_id} r={r} role={role}
            onRecord={(x) => setRecording({ kid: x, month: data.month })}
            onFee={(x) => setFeeFor({ kid: x, month: data.month })}
            onHistory={setHistoryKid} />
        ))}
      </div>
      <RecordPaymentDialog target={recording} onClose={() => setRecording(null)} token={token} onDone={done} />
      <FeeDialog target={feeFor} onClose={() => setFeeFor(null)} token={token} onDone={done} />
      <KidHistoryDialog kid={historyKid} onClose={() => setHistoryKid(null)} token={token} refreshKey={refreshKey}
        onCorrect={historyKid?.can_record ? (p, m) => setRecording({ kid: historyKid, month: m, payment: p }) : null} />
      {role === 'transport' && <PaymentLogDialog open={showLog} onClose={() => setShowLog(false)} token={token} />}
    </section>
  );
};
